<script setup lang="js">
import {computed, onMounted, ref} from 'vue';

import AppMenuItem from './AppMenuItem.vue';
import {useEventBus, useMittEvents} from "@/utils/EventBus.ts";
import {UserService} from "@/service/UserService.ts";
import {ConfigurationService} from "@/service/ConfigurationService.ts";
import {usePermissionStore} from "@/stores/usePermissionStore";

const permissionStore = usePermissionStore();
const configurationService = new ConfigurationService();
const featureFlags = ref({ auditingEnabled: false });

const menuItems = ref([
  {
    label: 'Home',
    items: [
      {label: 'Dashboard', icon: 'pi pi-fw pi-home', to: '/dashboard'},
      {label: 'Process Mail', icon: 'fa fa-solid fa-envelopes-bulk', to: '/process-mail', permission: 'mail.create'},
      {label: 'Process Mail Out', icon: 'fa fa-solid fa-person-walking-arrow-right', to: '/process-mail-out', permission: 'mail.process'},
      {label: 'Payment Due', icon: 'fa-solid fa-money-check-dollar', to: '/payment-due', badge: 0, badgeSeverity: 'warn', permission: 'mailbox.view'},
      {label: 'Refunds', icon: 'pi pi-fw pi-replay', to: '/refunds', permission: 'payment.view'},
      {label: 'Tasks', icon: 'fa fa-solid fa-list-check', to: '/tasks', badge: 0},
      {label: 'Expiring IDs', icon: 'fa fa-solid fa-id-card', to: '/expiring-ids', badge: 0, badgeSeverity: 'warn', permission: 'mailbox.view'},
      {label: 'Missing Data', icon: 'fa fa-solid fa-file-half-dashed', to: '/missing-data', badge: 0, badgeSeverity: 'warn', permission: 'mailbox.view'},
    ]
  },
  {
    label: 'Mailboxes',
    items: [
      {label: 'Requests', icon: 'pi pi-fw pi-inbox', to: '/mailbox-requests', badge: 0, permission: 'mailbox.view'},
      {label: 'Accounts', icon: 'pi pi-fw pi-server', to: '/mailboxes', permission: 'mailbox.view'},
    ]
  },
  {
    label: 'Settings',
    items: [
      {label: 'Mail Centers', icon: 'pi pi-fw pi-building', to: '/mail-centers', permission: 'mailcenter.view'},
      {label: 'Mailbox Plans', icon: 'pi pi-fw pi-money-bill', to: '/mailbox-plans', permission: 'mailbox_plan.view'},
      {label: 'Coupons', icon: 'pi pi-fw pi-ticket', to: '/coupons', permission: 'coupon.view'},
      {label: 'Agents', icon: 'pi pi-fw fa-solid fa-user-tie', to: '/agents', permission: 'agent.view'},
      {label: 'Users', icon: 'pi pi-fw fa-solid fa-user-gear', to: '/users', permission: 'user.view'},
      {label: 'Roles & Permissions', icon: 'pi pi-fw pi-shield', to: '/roles', permission: 'role.view'},
      {label: 'Audit Logs', icon: 'pi pi-fw pi-file-edit', to: '/audit-logs', permission: 'audit.view'},
    ]
  },

]);

// Filter menu items based on user permissions and feature flags
const model = computed(() => {
  return menuItems.value.map(section => {
    return {
      ...section,
      items: section.items.filter(item => {
        // Hide Audit Logs if auditing is disabled
        if (item.label === 'Audit Logs' && !featureFlags.value.auditingEnabled) {
          return false;
        }

        // If no permission is specified, show the item
        if (!item.permission) return true;
        // Otherwise, check if user has the required permission
        return permissionStore.hasPermission(item.permission);
      })
    };
  }).filter(section => section.items.length > 0); // Hide empty sections
});

function entry(menu, item) {
  return model.value.find(m => m.label === menu)?.items.find(i => i.label === item);
}

const userService = new UserService();
async function refreshBadges() {
  const {requests, tasks, expiringIds, missingData, overDueAccounts} = await userService.getNotifications();
  entry('Mailboxes', 'Requests').badge = requests;
  entry('Home', 'Payment Due').badge = overDueAccounts;
  entry('Home', 'Tasks').badge = tasks;
  entry('Home', 'Expiring IDs').badge = expiringIds;
  entry('Home', 'Missing Data').badge = missingData;
}

useMittEvents(useEventBus(), {
  onRequestsUpdated: refreshBadges,
  onTasksUpdated: refreshBadges,
  onExpiringIdUpdated: refreshBadges,
  onPaymentRecorded: refreshBadges
});


onMounted(async () => {
  // Fetch feature flags
  try {
    featureFlags.value = await configurationService.getFeatureFlags();
  } catch (error) {
    console.error('Failed to fetch feature flags:', error);
    // Keep default values if fetch fails
  }

  await refreshBadges();
});
</script>

<template>
  <ul class="layout-menu">
    <template v-for="(item, i) in model" :key="item">
      <app-menu-item v-if="!item.separator" :item="item" :index="i"></app-menu-item>
      <li v-if="item.separator" class="menu-separator"></li>
    </template>
  </ul>
</template>

<style lang="scss" scoped></style>
